import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import ConfirmDeleteDialog from '../../components/admin/ConfirmDeleteDialog';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type SuperUserTimeline } from '../../lib/api';
import { relativeTime } from '../../lib/dashboardHelpers';
import { C2Button } from '../../components/c2';

export default function AdminUserDetailPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<SuperUserTimeline | null>(null);
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [error, setError] = useState<string | null>(null);
  const [roleBusy, setRoleBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = () => {
    if (!id) return;
    api
      .superUserTimeline(id)
      .then(d => {
        setData(d);
        // Backend returns role only via /super/users listing — for the role toggle we'd
        // ideally have it on the detail too. The /super/users/:id/timeline payload doesn't
        // include role, so we default to 'user' until we re-fetch from /super/users below.
      })
      .catch(err => setError((err as Error).message || 'Failed to load user'));
  };

  // Also pull the user list once so we know the current role.
  useEffect(() => {
    if (!gate.ready || !id) return;
    load();
    api.superUsers().then(users => {
      const u = users.find(u => String(u.id) === String(id));
      if (u) setRole(u.role === 'admin' ? 'admin' : 'user');
    }).catch(() => { /* non-fatal */ });
  }, [gate.ready, id]);

  if (!gate.ready) return null;

  const toggleRole = async () => {
    if (!id) return;
    const next = role === 'admin' ? 'user' : 'admin';
    setRoleBusy(true);
    setError(null);
    try {
      await api.superSetUserRole(id, next);
      setRole(next);
    } catch (err) {
      setError((err as Error).message || 'Could not change role');
    } finally {
      setRoleBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    await api.superDeleteUser(id);
    navigate('/admin/users', { replace: true });
  };

  return (
    <AdminShell
      title={data ? data.user.email : 'User'}
      subtitle={
        data
          ? `${data.user.name || '—'} · joined ${relativeTime(data.user.created_at)}`
          : undefined
      }
      rightSlot={
        <Link
          to="/admin/users"
          className="text-[13px] font-semibold no-underline"
          style={{ color: 'var(--color-ink-soft)' }}
        >
          ← All users
        </Link>
      }
    >
      {error && (
        <div
          style={{
            padding: 14,
            marginBottom: 16,
            background: 'var(--color-peach)',
            color: 'var(--color-peach-deep)',
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!data ? (
        <div className="text-ink-soft" style={{ padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* User info card */}
          <div
            className="bg-surface mb-6"
            style={{
              padding: 20,
              border: '1px solid var(--color-hairline)',
              borderRadius: 16,
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <KV k="ID" v={String(data.user.id)} />
              <KV k="Role" v={role} />
              <KV k="Email" v={data.user.email} />
              <KV
                k="Extension last seen"
                v={
                  data.user.extension_last_seen
                    ? `${relativeTime(data.user.extension_last_seen)} (${data.user.extension_last_seen})`
                    : 'never'
                }
              />
              <KV
                k="First link"
                v={
                  data.firstLink
                    ? `go/${data.firstLink.slug} (${relativeTime(data.firstLink.created_at)})`
                    : 'none'
                }
              />
              <KV k="Joined" v={data.user.created_at} />
            </div>

            <div className="flex gap-2 mt-4">
              <C2Button
                variant="outline"
                size="sm"
                onClick={() => { void toggleRole(); }}
                disabled={roleBusy}
              >
                {roleBusy ? 'Updating…' : role === 'admin' ? 'Demote to user' : 'Promote to admin'}
              </C2Button>
            </div>
          </div>

          {/* Onboarding timeline */}
          <div
            className="bg-surface mb-6 overflow-hidden"
            style={{
              border: '1px solid var(--color-hairline)',
              borderRadius: 16,
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid var(--color-hairline)',
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--color-ink)',
              }}
            >
              Onboarding timeline ({data.events.length} events)
            </div>
            {data.events.length === 0 ? (
              <div className="text-ink-muted text-sm" style={{ padding: 32, textAlign: 'center' }}>
                No events recorded.
              </div>
            ) : (
              data.events.map((e, i) => (
                <div
                  key={i}
                  className="grid"
                  style={{
                    gridTemplateColumns: '200px 1fr 160px',
                    gap: 12,
                    padding: '10px 20px',
                    borderBottom: '1px solid var(--color-hairline)',
                    alignItems: 'baseline',
                  }}
                >
                  <span className="font-mono text-sm font-semibold text-ink">{e.event}</span>
                  <span className="text-xs text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap font-mono">
                    {e.metadata ?? ''}
                  </span>
                  <span className="text-xs text-ink-muted text-right">
                    {relativeTime(e.created_at)}
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Danger zone */}
          <div
            className="bg-surface"
            style={{
              padding: 20,
              border: '1px solid var(--color-peach)',
              borderRadius: 16,
            }}
          >
            <h3
              className="font-display font-bold m-0 mb-1"
              style={{ fontSize: 16, color: 'var(--color-peach-deep)' }}
            >
              Danger zone
            </h3>
            <p className="text-sm text-ink-soft m-0 mb-3">
              Delete this user and cascade-clean their links, click history, notifications, and
              password resets. Cannot be used on a super admin.
            </p>
            <C2Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              style={{ color: 'var(--color-peach-deep)' }}
            >
              Delete user
            </C2Button>
          </div>

          <ConfirmDeleteDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            title="Delete user"
            description={`This will permanently delete ${data.user.email} and everything tied to them.`}
            confirmPhrase={data.user.email}
            consequences={[
              'All links this user created will be deleted',
              'Their click history, notifications, onboarding events, and password resets will be deleted',
              'If they were the only member of a personal workspace, that workspace is deleted too',
              'Not reversible',
            ]}
            onConfirm={handleDelete}
          />
        </>
      )}
    </AdminShell>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div
        className="font-semibold uppercase mb-1"
        style={{ fontSize: 10.5, color: 'var(--color-ink-muted)', letterSpacing: '0.06em' }}
      >
        {k}
      </div>
      <div className="text-sm text-ink font-mono break-all">{v}</div>
    </div>
  );
}
